import StackTrace from 'stacktrace-js';
import jwtDecode from 'jwt-decode';
import { useHistory, useLocation } from 'react-router-dom';
import { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import { Button, Card, CardActions, CardContent, CardHeader, TextField, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useLocalStorage } from '../../services/hooks/useLocalStorage';
import { trackPromise, usePromiseTracker } from 'react-promise-tracker';

import { STC_ID, STRATUS_ID } from '../../services/constants';

import Header from '../Header';
import Loading from '../Loading';
import TenantSelector from '../TenantSelector';
import Cognito from '../Cognito';
import PinAndCardAuth from '../PinAndCardAuth';
import Workflows from '../Workflows';
import RocketImg from '../../img/rocket.png';

import {
    getTenants,
    getUserPoolAll,
    getWorkflows,
    hasTokenExpired,
    setMFPUserSettings,
    refreshToken as refreshT,
    isVersionValid,
    getCookieDeletionList,
    addCookieToMFPList,
    restartApp,
    getLicenseUsage,
    sendLogsToMfp,
} from '../../services/api';

const MFP_ADDRESS_DEBUG_OVERRIDE = localStorage.getItem('mfpAddressOverride', '')
const REACT_APP_MFP_ADDRESS_OVERRIDE = process.env.REACT_APP_MFP_ADDRESS_OVERRIDE
const MFP_ADDRESS = MFP_ADDRESS_DEBUG_OVERRIDE ? MFP_ADDRESS_DEBUG_OVERRIDE :
    (REACT_APP_MFP_ADDRESS_OVERRIDE ? REACT_APP_MFP_ADDRESS_OVERRIDE : 'http://127.0.0.1:8090');

const useStyles = makeStyles({
    controls: {
        display: 'inline-flex',
        justifyContent: 'end',
        width: '73%'
    },
    header: {
        background: 'black',
        color: 'white',
        height: 32,
        padding: 8
    },
    logo: {
        display: 'inline-flex'
    },
    resultContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '400px',
        '& *': {
            margin: '12px'
        }
    },
    resultText: {
        maxWidth: '600px',
        textAlign: 'center'
    },
    title: {
        fontSize: '20px',
        margin: 8
    },
    toolbar: {
        alignContent: 'space-around',
        background: 'lightgray',
        display: 'flex',
        height: '48px',
        justifyContent: 'flex-end'
    },
    page: {
        position: 'relative',
        top: 65
    },
    card: {
        display: 'flex',
        flexDirection: 'column',
        width: '75vw',
        height: '52vh',
        position: 'absolute',
        top: '26%',
        left: '12.5%',
        zIndex: '5'
    },
    cardBackground: {
        background: '#00000060',
        width: '100%',
        height: '100%',
        top: '0',
        position: 'absolute',
        zIndex: '3'
    },
    debugCard: {
        width: '90%',
        position: 'absolute',
        top: '0',
        left: '5%',
        zIndex: '5'
    },
    debugLine: {
        margin: '8px 0'
    },
    body: {
        overflow: 'hidden'
    }
});

const MFP = () => {
    const history = useHistory();
    const location = useLocation();
    const classes = useStyles();
    const [stratus] = useLocalStorage("stratus", false);
    const ns = stratus ? 'stratus' : 'scantrip';
    const { t } = useTranslation(ns);
    const [wfx, setWFX] = useState(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [tenants, setTenants] = useState(undefined);
    const [noDipa, setNoDipa] = useLocalStorage("noDipa", false);
    const [user, setUser] = useLocalStorage('user', '');
    const [tenant, setTenant] = useLocalStorage('tenant', undefined);
    const [tenantSelected, setTenantSelected] = useState(false);
    const [accessToken, setAccessToken] = useLocalStorage('accessToken', undefined);
    const [idToken, setIdToken] = useLocalStorage('idToken', undefined);
    const [refreshToken, setRefreshToken] = useLocalStorage('refreshToken', undefined);
    const [client, setClient] = useLocalStorage('client', undefined);
    const [errorText, setErrorText] = useState(undefined);
    const [errorFunction, setErrorFunction] = useState(undefined);
    const [debugDisplay, setDebugDisplay] = useState(false);
    const [debugHash, setDebugHash] = useLocalStorage('debugHash', undefined);
    const [settingsFetched, setSettingsFetched] = useState(false);
    const [appVersion] = useLocalStorage('appVersion', '')
    const [generation] = useLocalStorage('generation', -1)
    const [hasScanner] = useLocalStorage('hasScanner', true);
    const [loggedInFromAuth] = useLocalStorage('loggedInFromAuth', false);
    const [pinSkip, setPinSkip] = useState(false);
    const [lastScannedCardId, setLastScannedCardId] = useState(undefined);
    const [autostartWorkflow, setAutostartWorkflow] = useState(true);
    const { promiseInProgress } = usePromiseTracker({ delay: 250 })

    const showLoading = () => {
        return promiseInProgress || isLoading
    }

    const showError = (text, fn) => {
        setErrorText(text);
        setErrorFunction(fn);
    }

    // eslint-disable-next-line
    const closeError = () => {
        setErrorText(undefined);
    }

    const saveUserInfoToMFP = async (settings = { accessToken, idToken, refreshToken, user, client, tenant }) => {
        let appId = STC_ID;
        if (stratus) {
            appId = STRATUS_ID;
        }
        await setMFPUserSettings(settings, appId);
    }

    const generateNewAccessToken = async () => {
        if (!tenant) {
            console.log('generateNewAccessToken called without tenant set!');
            return
        }
        const tokenResp = await refreshT(tenant.slug, refreshToken, client.ClientId, client.ClientSecret, tenant.region);
        setIdToken(tokenResp.id_token);
        setAccessToken(tokenResp.access_token);
        saveUserInfoToMFP({
            accessToken: tokenResp.access_token,
            idToken: tokenResp.id_token,
            refreshToken,
            user,
            client,
            tenant
        });
        return tokenResp.access_token;
    }

    const checkAccessToken = async () => {
        try {
            if (!accessToken || !tenant) {
                showError(t('Your session has expired. Please log in and try again.'), () => cbLogout);
                return null
            }

            if (noDipa && hasTokenExpired(accessToken, 1800)) {
                console.log('Refreshing token');

                try {
                    return await generateNewAccessToken();
                } catch (e) {
                    console.error(e);
                    showError(t(`Your session has expired. Please log in and try again.`), () => cbLogout);
                    return null;
                }
            }

            return accessToken;
        } catch (e) {
            console.error(e);
            alert('Error refreshing token');
            alert(e);
            const callback = (stackframes) => {
                const stringifiedStack = stackframes.map(sf => {
                    return sf.toString();
                }).join('\n');
                alert(stringifiedStack);
            }
            const errback = (_) => {
            }
            StackTrace.fromError(e).then(callback).catch(errback);
        }
    }

    const redirectToMfp = async (id) => {
        let appId = STC_ID;
        if (stratus) {
            appId = STRATUS_ID;
        }
        setIsLoading(true);
        const token = await trackPromise(checkAccessToken());
        if (!token) {
            showError(t('Your session has expired. Please log in again to continue.'), () => logout);
            setIsLoading(false);
            return;
        }
        const response = await trackPromise(getLicenseUsage(tenant, appId));
        if (response.licenseUsage) {
            showError(t('You have reached a daily limit for your Dispatcher ScanTrip Cloud Trial License. Trial Licenses are limited to 50 files or 10 MB total per day.'),
                () => cbLogout);
            setIsLoading(false);
            return;
        } else if (response.expired) {
            showError(t('Your license has expired. To continue using Dispatcher ScanTrip Cloud, please contact your Konica Minolta representative about upgrading your license.'),
                () => cbLogout);
            setIsLoading(false);
            return;
        } else if (response.unlicensed) {
            showError(t('The device you are using does not have a Dispatcher ScanTrip Cloud Device License assigned to it. Please add a Device License to this device to continue.'),
                () => cbLogout);
            setIsLoading(false);
            return;
        }
        // Values needed when redirecting to MFP:
        // 1. workflowId
        // 2. metadata
        // 2.a. user
        // 2.b. tenant
        // 2.b.i name
        // 2.b.ii id
        // 2.b.iii region
        // 3. authToken
        // 4. stratus
        // 5. dipa
        // 6. hideLogout
        const metadata = JSON.stringify({
            user,
            tenant,
            authToken: accessToken,
        });
        const params = {
            workflowId: id,
            metadata,
            stratus,
            dipa: !noDipa,
            hideLogout: loggedInFromAuth
        }
        const url = new URL(`${MFP_ADDRESS}/${appId}/scan.html`)
        const searchParams = new URLSearchParams(params);
        url.search = searchParams.toString();
        window.location = url;
    }

    const clearLocationState = () => {
        history.replace({ ...history.location, state: undefined });
    }
    const cbClearLocationState = useCallback(clearLocationState, [history]);

    const workflowResult = location.state?.success;
    const workflowList = () => {
        return (
            showLoading() ? (
                <>
                    <Loading clickFunction={handleDebugLog} />
                    <Workflows tenant={tenant} workflows={wfx} startWfx={redirectToMfp} autostart={autostartWorkflow} debugHandler={handleDebugLog} />
                </>
            ) : (
                <>
                    <Workflows tenant={tenant} workflows={wfx} startWfx={redirectToMfp} autostart={autostartWorkflow} debugHandler={handleDebugLog} />
                </>
            )
        )
    }
    const result = () => {
        return (
            workflowResult ? (
                <div className={classes.resultContainer}>
                    <img src={RocketImg} alt="Success" />
                    <div className={classes.resultText}>{t('Your document has been scanned, processed, and sent to the selected destination.')}</div>
                    <Button variant="contained" onClick={() => clearLocationState()}>{t('Continue')}</Button>
                </div>
            ) : (
                <div className={classes.resultContainer}>
                    <div className={classes.resultText}>{t('An error has occurred while processing your document. Please consult the workflow logs to determine the location and nature of the error.')}</div>
                    <Button variant="contained" onClick={() => clearLocationState()}>{t('Continue')}</Button>
                </div>
            )
        );
    }

    const selectTenant = async (t) => {
        if (!t) {
            return
        }
        const resp = await trackPromise(getUserPoolAll(t.slug, t.region, t.id));
        t.userPoolId = resp.poolId;
        t.clientId = resp.clientId;
        t.customClientId = resp.customClientId;
        t.customClient = resp.customClient;
        t.userPool = resp.userPool;
        t.client = resp.client;
        t.iwsAuth = resp.iwsAuth
        setTenantSelected(true);
        setTenant(t);
    }

    const logout = async () => {
        cbClearLocationState();
        setSettingsFetched(false);
        history.push("/logout");
    }

    const cbLogout = useCallback(logout, [cbClearLocationState, history, setSettingsFetched]);

    const goToMarketPlace = () => {
        window.location = `${MFP_ADDRESS}/0A0243A9`;
    }

    const redirect = () => {
        history.push('./dipa');
    }

    const pageSelect = () => {
        if (errorText) {
            return <></>
        }
        if (!tenant) {
            return <TenantSelector tenants={tenants} loading={showLoading()} onClick={selectTenant} />
        } else if (!user && !location.state?.user) {
            if (tenantSelected) {
                const cardEnabled = tenant.iwsAuth?.cardEnabled && (generation !== 5 && generation !== 4)
                if (!pinSkip && (tenant.iwsAuth?.pinEnabled || (cardEnabled))) {
                    return <PinAndCardAuth
                        tenant={tenant}
                        setters={{ setUser, setAccessToken, setIdToken, setRefreshToken, setClient, setTenant, setLastScannedCardId }}
                        continue={setPinSkip}
                        pinEnabled={tenant.iwsAuth?.pinEnabled === true}
                        cardEnabled={cardEnabled} />
                }
                return <Cognito tenant={tenant} setters={{ setUser, setAccessToken, setIdToken, setRefreshToken, setClient, setTenant }} lastScannedCardId={lastScannedCardId} />
            } else {
                setTenant(undefined);
            }
        } else {
            return workflowResult === undefined ? workflowList() : result()
        }
    }

    const errorDialog = () => {
        if (!errorText) {
            return <></>
        }
        return (
            <div>
                <Card className={classes.card}>
                    <CardHeader
                        avatar={<InfoIcon color="primary" fontSize="large" />}
                        title={<Typography sx={{ fontSize: 'larger' }}>{t('Information')}</Typography>}
                    />
                    <CardContent sx={{ padding: '0' }}>
                        <Typography sx={{ padding: '0 66px', whiteSpace: 'pre-line', fontSize: '1.1rem' }}>
                            {errorText}
                        </Typography>
                    </CardContent>
                    <CardActions disableSpacing sx={{ mt: 'auto', justifyContent: 'flex-end' }}>
                        <Button variant="contained" sx={{ padding: '8px 32px', margin: '8px' }} onClick={() => { errorFunction() }}>{t('OK')}</Button>
                    </CardActions>
                </Card>
                <div className={classes.cardBackground}></div>
            </div>
        );
    }

    const handleDebugLog = useCallback(async () => {
        const debugBody = {
            logSourceComponent: 'MFP',
            domain: window.location.hostname,
            address: window.location.href,
            version: appVersion,
            promiseInProgress,
            workflows: wfx,
            tenants: tenants,
            noDipa,
            user,
            tenant,
            tenantSelected,
            accessToken,
            refreshToken,
            now: Math.floor(Date.now() / 1000),
            client,
            stratus,
            errorText,
            isLoading
        }
        await sendLogsToMfp(debugBody, stratus ? STRATUS_ID : STC_ID);
    }, [appVersion, promiseInProgress, wfx, tenants, noDipa, user, tenant, tenantSelected, accessToken, refreshToken, client, stratus, errorText, isLoading]);

    const displayDebug = () => {
        if (!debugDisplay) {
            return <></>
        }
        let decodedAccessToken = {}
        if (accessToken) {
            decodedAccessToken = jwtDecode(accessToken);
        }
        return (
            <div>
                <Card className={classes.debugCard}>
                    <CardContent>
                        <div>
                            <div className={classes.debugLine}>{`Debug display: ${debugDisplay}`}</div>
                            <div className={classes.debugLine}>{`Domain: ${window.location.hostname}`}</div>
                            <div className={classes.debugLine}>{`Version: ${appVersion}`}</div>
                            <div className={classes.debugLine}>{`promiseInProgress: ${promiseInProgress}`}</div>
                            <div className={classes.debugLine}>{`wfx: ${JSON.stringify(wfx)}`}</div>
                            <div className={classes.debugLine}>{`tenants: ${JSON.stringify(tenants)}`}</div>
                            <div className={classes.debugLine}>{`noDipa: ${noDipa}`}</div>
                            <div className={classes.debugLine}>{`user: ${user}`}</div>
                            <div className={classes.debugLine}>{`tenant: ${JSON.stringify(tenant)}`}</div>
                            <div className={classes.debugLine}>{`tenantSelect: ${tenantSelected}`}</div>
                            <div className={classes.debugLine}>{`accessToken: ${accessToken}`}</div>
                            <div className={classes.debugLine}>{`decodedAccessToken: ${JSON.stringify(decodedAccessToken)}`}</div>
                            <div className={classes.debugLine}>{`Date.now(): ${Math.floor(Date.now() / 1000)}`}</div>
                            <div className={classes.debugLine}>{`refreshToken: ${refreshToken}`}</div>
                            <div className={classes.debugLine}>{`client: ${JSON.stringify(client)}`}</div>
                            <div className={classes.debugLine}>{`stratus: ${stratus}`}</div>
                            <div className={classes.debugLine}>{`errorText: ${errorText}`}</div>
                            <TextField
                                id="debug-hash-input"
                                variant="outlined"
                                size="small"
                                value={debugHash}
                                onChange={(event) => setDebugHash(event.target.value)}
                            />
                        </div>
                    </CardContent>
                </Card>
            </div>
        )
    }

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const autostart = params.get('autostart');
        if (autostart !== null) {
            setAutostartWorkflow(autostart === 'true');
        }
    }, [location.search]);

    const cbSelectTenant = useCallback(selectTenant, [setTenant, setTenantSelected]);
    // eslint-disable-next-line
    const cbCheckAccessToken = useCallback(checkAccessToken, [accessToken, client, tenant, refreshToken, setAccessToken, setIdToken, user]);
    useEffect(() => {
        // Check if app version number is up to date
        // TODO Fix version check with Paragon
        if (!isVersionValid(appVersion) && noDipa) {
            showError(t('An update to your app is required to continue.'), () => goToMarketPlace);
            return;
        }
        const stateTenant = location.state?.tenant;
        const stateUser = location.state?.user;
        if (location.state?.noDipa) {
            setNoDipa(true);
        }
        const stateAccessToken = location.state?.accessToken;
        const stateIdToken = location.state?.idToken;
        const stateRefreshToken = location.state?.refreshToken;
        const stateClient = location.state?.client;
        if (stateTenant && stateUser) {
            setUser(stateUser);
            setTenant(stateTenant);
            setAccessToken(stateAccessToken);
            setIdToken(stateIdToken);
            setRefreshToken(stateRefreshToken);
            setClient(stateClient);
            saveUserInfoToMFP({
                accessToken: stateAccessToken,
                idToken: stateIdToken,
                refreshToken: stateRefreshToken,
                user: stateUser,
                client: stateClient,
                tenant: stateTenant
            });
        }
        async function fetchTenants() {
            let appId = STC_ID;
            if (stratus) {
                appId = STRATUS_ID;
            }
            const tlist = await getTenants(appId);
            // TODO Fix this filter to support multiple plans
            const filteredList = tlist.filter(t => stratus ? t?.plan === 'stratus' : t?.plan !== 'stratus');
            // Compare tenant list to list of deleted cookies
            const cookieList = await getCookieDeletionList(appId);
            const authURLList = [];
            const authURLs = tlist.map((l) => {
                const url = `${l.slug}.auth.${l.region}.amazoncognito.com`;
                authURLList.push(url);
                return url;
            });
            if (cookieList.length < authURLs.length) {
                const cookieResult = await addCookieToMFPList(authURLList, appId);
                if (cookieResult) {
                    await restartApp(appId);
                }
            }
            for (const url of authURLs) {
                let found = false;
                for (const cookie of cookieList) {
                    if (cookie === url) {
                        found = true;
                        break;
                    }
                }
                if (!found) {
                    const cookieResult = await addCookieToMFPList(authURLList, appId);
                    if (cookieResult) {
                        await restartApp(appId);
                    }
                }
            }
            // If we only have one tenant in the list, selected it.
            // We don't need to set the tenants list. Not setting the list will hide the tenant
            // from briefly appearing before we redirect to the tenant auth flow.
            if (filteredList.length === 1) {
                cbSelectTenant(filteredList[0]);
            } else {
                setTenants(filteredList);
            }
        }
        if (tenants === undefined && tenant === undefined) {
            fetchTenants();
        }
        // eslint-disable-next-line
    }, [
        cbSelectTenant,
        location,
        setAccessToken,
        setClient,
        setIdToken,
        setRefreshToken,
        setTenant,
        setUser,
        tenants,
        tenant,
        setNoDipa,
        settingsFetched,
        setSettingsFetched,
        appVersion
    ]);

    async function fetchWorkflows() {
        try {
            const token = await cbCheckAccessToken();
            if (!hasScanner) {
                setWFX([])
                return
            }

            if (token && tenant) {
                let appId = STC_ID;
                if (stratus) {
                    appId = STRATUS_ID;
                }
                const workflowResp = await getWorkflows(token, tenant.region, appId);
                // TODO Different errors for different responses
                if (workflowResp.status !== 200) {
                    showError(t('Fetching workflows failed. Your session has expired. Please log in again to continue.'), () => cbLogout);
                    return;
                }
                setWFX(workflowResp.workflows);
            } else {
                setWFX([]);
            }

        } catch (e) {
            setWFX([]);
            console.error(e);
            showError(t(`There was an error attempting to fetch your workflows. Please log in and try again.
                    Additional error information: ${e}`), () => cbLogout);
        }
    }

    useEffect(() => {
        if (!user) {
            return;
        } else if (tenant && !Array.isArray(wfx)) {
            fetchWorkflows();
        }
        // eslint-disable-next-line
    }, [
        accessToken,
        tenant,
        wfx,
        user
    ]);

    const handleKeyPress = useCallback((event) => {
        if (event.ctrlKey && event.code === "KeyJ") {
            setDebugDisplay(!debugDisplay);
        }
        if (event.ctrlKey && event.code === "KeyL") {
            handleDebugLog();
        }
    // eslint-disable-next-line
    }, [debugDisplay, handleDebugLog, setAccessToken, setRefreshToken]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyPress);

        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [handleKeyPress]);

    useEffect(() => {
        document.body.classList.add(classes.body);
    }, [classes.body]);

    return (
        <>
            <Header user={user} tenant={tenant?.name} logout={logout} redirect={redirect} noDipa={noDipa} />
            <div className={classes.page}>
                {pageSelect()}
            </div>
            <div>{errorDialog()}</div>
            <div>{displayDebug()}</div>
        </>
    )
}

export default MFP